// Explicit coordinate space definitions and utilities

export enum CoordinateSpace {
  CONTENT = 'content',    // Origin: top-left of content rectangle (inside margins)
  PAGE = 'page',         // Origin: top-left of page
  VIEWPORT = 'viewport', // Origin: top-left of visible canvas
  DEVICE = 'device',     // Origin: top-left of canvas backing store
  HARFBUZZ = 'harfbuzz'  // Origin: HarfBuzz typesetter coordinate space (~1.67x larger than Canvas)
}

export interface ContentRect {
  x: number;      // Page space X offset
  y: number;      // Page space Y offset  
  width: number;  // Content area width
  height: number; // Content area height
}

export interface Coordinates {
  x: number;
  y: number;
  space: CoordinateSpace;
}

export interface PageDimensions {
  width: number;
  height: number;
  contentRect: ContentRect;
}

/**
 * Utility functions for coordinate space validation and debugging
 */
export class CoordinateSpaceUtils {
  /**
   * Validate that coordinates have explicit space designation
   */
  static validateCoordinates(coords: Coordinates): void {
    if (!coords.space) {
      throw new Error('Coordinates must have explicit space designation');
    }
    
    if (!Object.values(CoordinateSpace).includes(coords.space)) {
      throw new Error(`Invalid coordinate space: ${coords.space}`);
    }
    
    if (typeof coords.x !== 'number' || typeof coords.y !== 'number') {
      throw new Error('Coordinates must have numeric x and y values');
    }
  }

  /**
   * Create coordinates with explicit space validation
   */
  static createCoordinates(x: number, y: number, space: CoordinateSpace): Coordinates {
    const coords: Coordinates = { x, y, space };
    this.validateCoordinates(coords);
    return coords;
  }

  /**
   * Debug logging for coordinate transforms
   */
  static logTransform(
    from: Coordinates,
    to: Coordinates,
    transformType: string,
    context?: any
  ): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔄 [CoordinateTransform] ${transformType}:`, {
        from: `${from.space}(${from.x.toFixed(1)}, ${from.y.toFixed(1)})`,
        to: `${to.space}(${to.x.toFixed(1)}, ${to.y.toFixed(1)})`,
        context
      });
    }
  }

  /**
   * Validate content rectangle
   */
  static validateContentRect(contentRect: ContentRect): void {
    if (typeof contentRect.x !== 'number' || typeof contentRect.y !== 'number') {
      throw new Error('ContentRect must have numeric x and y coordinates');
    }
    
    if (typeof contentRect.width !== 'number' || typeof contentRect.height !== 'number') {
      throw new Error('ContentRect must have numeric width and height');
    }
    
    if (contentRect.width <= 0 || contentRect.height <= 0) {
      throw new Error('ContentRect must have positive width and height');
    }
  }

  /**
   * Create content rectangle with validation
   */
  static createContentRect(x: number, y: number, width: number, height: number): ContentRect {
    const contentRect: ContentRect = { x, y, width, height };
    this.validateContentRect(contentRect);
    return contentRect;
  }
}
